import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton, StyledSegmentedControl, StyledSegmentButton } from './SectionBar.styled';

interface NO4ReportFiltersProps {
  customFilters: {
    serviceTypes?: string[];
    accountingTypes?: string[];
    year?: string;
    direction?: string;
    onServiceTypesChange?: (values: string[]) => void;
    onAccountingTypesChange?: (values: string[]) => void;
    onYearChange?: (year: string) => void;
    onDirectionChange?: (direction: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
    yearOptions?: string[];
  };
}

const NO4ReportFilters: React.FC<NO4ReportFiltersProps> = ({ customFilters }) => {
  // State for dropdown menus
  const [serviceTypesAnchorEl, setServiceTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [accountingTypesAnchorEl, setAccountingTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [yearAnchorEl, setYearAnchorEl] = React.useState<null | HTMLElement>(null);

  const isServiceTypesMenuOpen = Boolean(serviceTypesAnchorEl);
  const isAccountingTypesMenuOpen = Boolean(accountingTypesAnchorEl);
  const isYearMenuOpen = Boolean(yearAnchorEl);

  // Generate year options from 2008 to current year
  // Use cascaded options from parent if provided, otherwise generate all options
  const yearOptions = React.useMemo(() => {
    // If parent provides cascaded year options, use them
    if (customFilters.yearOptions && customFilters.yearOptions.length > 0) {
      return customFilters.yearOptions.map(year => ({ value: year, label: year }));
    }
    
    // Otherwise, generate all options
    const options: { value: string; label: string }[] = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= 2008; year--) { // Reverse order to show recent years first
      options.push({
        value: year.toString(),
        label: year.toString()
      });
    }
    
    return options;
  }, [customFilters.yearOptions]);

  // Filter options
  const serviceTypeOptions = [
    { id: 'A', label: 'A - Airmail Priority' },
    { id: 'B', label: 'B - SAL Non-Priority' },
    { id: 'C', label: 'C - Surface Non-Priority' },
    { id: 'D', label: 'D - Surface Priority' },
  ];

  const accountingTypeOptions = [
    { id: 'G', label: 'Gross' },
    { id: 'N', label: 'Net' },
  ];

  const directionOptions = [
    { id: 'All', label: 'All' },
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  // Handlers
  const handleServiceTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setServiceTypesAnchorEl(event.currentTarget);
  };

  const handleServiceTypesMenuClose = () => {
    setServiceTypesAnchorEl(null);
  };

  const handleAccountingTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setAccountingTypesAnchorEl(event.currentTarget);
  };

  const handleAccountingTypesMenuClose = () => {
    setAccountingTypesAnchorEl(null);
  };

  const handleYearClick = (event: React.MouseEvent<HTMLElement>) => {
    setYearAnchorEl(event.currentTarget);
  };

  const handleYearMenuClose = () => {
    setYearAnchorEl(null);
  };

  const handleYearChange = (year: string) => {
    customFilters.onYearChange?.(year);
    handleYearMenuClose();
  };

  const handleServiceTypeToggle = (value: string) => {
    const currentValues = customFilters.serviceTypes || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onServiceTypesChange?.(newValues);
  };

  const handleAccountingTypeToggle = (value: string) => {
    const currentValues = customFilters.accountingTypes || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onAccountingTypesChange?.(newValues);
  };

  const handleDirectionChange = (direction: string) => {
    customFilters.onDirectionChange?.(direction);
  };

  // Display labels
  const getYearLabel = () => {
    if (!customFilters.year) return 'Select Year';
    return customFilters.year;
  };

  const getServiceTypesLabel = () => {
    const selected = customFilters.serviceTypes || [];
    if (selected.length === 0) return 'Select Mail Categories';
    if (selected.length === serviceTypeOptions.length) return 'All Mail Categories';
    return `${selected.length} Mail Categories`;
  };

  const getAccountingTypesLabel = () => {
    const selected = customFilters.accountingTypes || [];
    if (selected.length === 0) return 'Select Accounting Types';
    if (selected.length === accountingTypeOptions.length) return 'All Accounting Types';
    return `${selected.length} Accounting Types`;
  };

  const getDirectionLabel = () => {
    return customFilters.direction || 'All';
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Year Filter */}
      <FilterButton
        onClick={handleYearClick}
        endIcon={<FontAwesomeIcon icon={faChevronDown} />}
        variant="outlined"
        size="small"
      >
        {getYearLabel()}
      </FilterButton>
      <Menu
        anchorEl={yearAnchorEl}
        open={isYearMenuOpen}
        onClose={handleYearMenuClose}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: 120,
          },
        }}
      >
        <MenuItem onClick={() => handleYearChange('')}>
          <Typography variant="body2">Clear</Typography>
        </MenuItem>
        {yearOptions.map((option) => (
          <MenuItem key={option.value} onClick={() => handleYearChange(option.value)}>
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Direction Filter */}
      <StyledSegmentedControl>
        {directionOptions.map((option) => (
          <StyledSegmentButton
            key={option.id}
            active={customFilters.direction === option.id}
            onClick={() => handleDirectionChange(option.id)}
          >
            {option.label}
          </StyledSegmentButton>
        ))}
      </StyledSegmentedControl>

      {/* Mail Categories Filter */}
      <FilterButton
        onClick={handleServiceTypesClick}
        endIcon={<FontAwesomeIcon icon={faChevronDown} />}
        variant="outlined"
        size="small"
      >
        {getServiceTypesLabel()}
      </FilterButton>
      <Menu
        anchorEl={serviceTypesAnchorEl}
        open={isServiceTypesMenuOpen}
        onClose={handleServiceTypesMenuClose}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: 200,
          },
        }}
      >
        {serviceTypeOptions.map((option) => (
          <MenuItem key={option.id} dense>
            <Checkbox
              checked={(customFilters.serviceTypes || []).includes(option.id)}
              onChange={() => handleServiceTypeToggle(option.id)}
              size="small"
            />
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Accounting Types Filter */}
      <FilterButton
        onClick={handleAccountingTypesClick}
        endIcon={<FontAwesomeIcon icon={faChevronDown} />}
        variant="outlined"
        size="small"
      >
        {getAccountingTypesLabel()}
      </FilterButton>
      <Menu
        anchorEl={accountingTypesAnchorEl}
        open={isAccountingTypesMenuOpen}
        onClose={handleAccountingTypesMenuClose}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: 200,
          },
        }}
      >
        {accountingTypeOptions.map((option) => (
          <MenuItem key={option.id} dense>
            <Checkbox
              checked={(customFilters.accountingTypes || []).includes(option.id)}
              onChange={() => handleAccountingTypeToggle(option.id)}
              size="small"
            />
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Export Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('HTML')}
          disabled={customFilters.isExporting}
          variant="contained"
          size="small"
        >
          {customFilters.isExporting ? (
            <CircularProgress size={16} />
          ) : (
            'HTML'
          )}
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('PDF')}
          disabled={customFilters.isExporting}
          variant="contained"
          size="small"
        >
          {customFilters.isExporting ? (
            <CircularProgress size={16} />
          ) : (
            'PDF'
          )}
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('XLS')}
          disabled={customFilters.isExporting}
          variant="contained"
          size="small"
        >
          {customFilters.isExporting ? (
            <CircularProgress size={16} />
          ) : (
            'XLS'
          )}
        </StyledActionButton>
      </Box>
    </Box>
  );
};

export default NO4ReportFilters;